import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { LabelChipGrid } from '@/components/ui/LabelChipGrid';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { ReaderAvatarPicker } from '@/components/readers/ReaderAvatarPicker';
import { DEFAULT_READER_AVATAR_SLUG, type ReaderAvatarSlug } from '@/constants/reader-avatars';
import { accountsService } from '@/services/accounts.service';
import { readersService } from '@/services/readers.service';
import { labelsService } from '@/services/labels.service';
import { useAccountStore, formatAccountName } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import { useGuestStore } from '@/store/guest.store';
import type { LabelView, ReaderView } from '@/types/api';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';

/**
 * Onboarding — three-step wizard rendered for accounts with no readers yet.
 * ProtectedRoute redirects to `/onboarding` whenever `readers.length === 0`
 * so the wizard is always reachable; on completion we exit by navigating to
 * `/library` and `setReaders([created])` lets the gate release.
 *
 * Step 1 — collect parent name (PATCH /accounts/me).
 * Step 2 — create the first reader (POST /readers).
 * Step 3 — pick interests + reading level in a single PUT /readers/:id/labels.
 *
 * We keep the wizard state local (no store) because a refresh before
 * completion just re-enters step 1 with blank fields, which is acceptable
 * for this one-time setup flow.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const account = useAccountStore((s) => s.account);
  const patchAccount = useAccountStore((s) => s.patchAccount);
  const setReaders = useReadersStore((s) => s.setReaders);
  const setSelectedReaderId = useReadersStore((s) => s.setSelectedReaderId);
  // Reading levels the visitor picked while guest-browsing, to pre-fill step 3.
  const guestLevels = useGuestStore((s) => s.readingLevels);
  const clearGuest = useGuestStore((s) => s.clear);

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 state. Start blank rather than seeding from `account` — the
  // account name is auto-derived from the Auth0 profile at signup (often a
  // placeholder such as the email local-part), so pre-filling it here looked
  // wrong. The parent types their real name and step 1 PATCHes /accounts/me.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Step 2 state
  const [readerName, setReaderName] = useState('');
  const [readerAvatarSlug, setReaderAvatarSlug] = useState<ReaderAvatarSlug>(
    DEFAULT_READER_AVATAR_SLUG,
  );
  const [creatingReader, setCreatingReader] = useState(false);
  const [createdReader, setCreatedReader] = useState<ReaderView | null>(null);

  // Step 3 state — reading level only. (Genre/topic selection was removed to
  // simplify reader setup; reading level is the sole interest we collect.)
  const [readingLevels, setReadingLevels] = useState<LabelView[]>([]);
  const [labelsLoaded, setLabelsLoaded] = useState(false);
  // Reading levels are multi-select: a single reader can span more than one
  // level (e.g. reads chapter books for stories they love, picture books
  // when tired), and the bookshelf RPC treats them as a union.
  const [selectedReadingLevelIds, setSelectedReadingLevelIds] = useState<Set<string>>(new Set());
  const [savingLabels, setSavingLabels] = useState(false);

  // Lazily load the reading-level catalogue when the user advances to step 3.
  useEffect(() => {
    if (step !== 3 || labelsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const levels = await labelsService.list('reading_level');
        if (cancelled) return;
        setReadingLevels(levels);
        setLabelsLoaded(true);
        // Carry over the reading levels picked while guest-browsing (matched
        // by label id), so this step lands pre-filled rather than blank.
        if (guestLevels.length > 0) {
          const guestIds = new Set(guestLevels.map((g) => g.id));
          const matched = levels.filter((l) => guestIds.has(l.id)).map((l) => l.id);
          if (matched.length > 0) setSelectedReadingLevelIds(new Set(matched));
        }
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Failed to load reading levels');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, labelsLoaded, guestLevels]);

  const handleStep1 = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) {
      toast.error('Please enter both a first and last name.');
      return;
    }
    setSavingName(true);
    try {
      const updated = await accountsService.updateMe({ firstName: first, lastName: last });
      patchAccount(updated);
      setStep(2);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save your name.');
    } finally {
      setSavingName(false);
    }
  };

  const handleStep2 = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = readerName.trim();
    if (!name) {
      toast.error("What's your reader's name?");
      return;
    }
    setCreatingReader(true);
    try {
      const reader = await readersService.create({ name, avatarSlug: readerAvatarSlug });
      setCreatedReader(reader);
      setStep(3);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create your reader.');
    } finally {
      setCreatingReader(false);
    }
  };

  const finish = async () => {
    if (!createdReader) return;
    const labelIds = [...selectedReadingLevelIds];

    setSavingLabels(true);
    try {
      if (labelIds.length > 0) {
        await readersService.setLabels(createdReader.id, { labelIds });
      }
      // Refetch readers so the store has the merged label arrays from the RPC.
      const readers = await readersService.list();
      setReaders(readers);
      setSelectedReaderId(createdReader.id);
      clearGuest(); // guest selection has been consumed
      toast.success(`Welcome to OllieReads, ${formatAccountName(account)}!`);
      navigate('/library', { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save interests.');
    } finally {
      setSavingLabels(false);
    }
  };

  const toggleReadingLevel = (id: string) =>
    setSelectedReadingLevelIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="relative min-h-screen bg-or-cream-50 bg-cover bg-center px-6 py-10"
      style={{ backgroundImage: 'url(/brand/books_login_bg.png)' }}
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-white/40 via-or-cream-50/60 to-white/70"
      />

      <div className="relative mx-auto flex w-full max-w-2xl flex-col items-center">
        <StepIndicator step={step} />
        <div className="mt-6 w-full rounded-[28px] border border-white/60 bg-white/95 p-8 shadow-or-pop backdrop-blur-sm">
          {step === 1 ? (
            <form onSubmit={handleStep1} className="flex flex-col gap-5">
              <Header
                eyebrow="Step 1 of 3"
                title="Let's get the basics right"
                subtitle="We use your name on the account page — not on anything your kids see."
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  label="First name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                  maxLength={120}
                />
                <FormField
                  label="Last name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={120}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <Button type="submit" size="lg" disabled={savingName}>
                  {savingName ? 'Saving…' : 'Continue'}
                </Button>
              </div>
            </form>
          ) : null}

          {step === 2 ? (
            <form onSubmit={handleStep2} className="flex flex-col gap-5">
              <Header
                eyebrow="Step 2 of 3"
                title="Add your first reader"
                subtitle="This is the profile we use to tailor recommendations. You can add more readers later."
              />
              <div className="flex items-center gap-4">
                <ReaderAvatar name={readerName || '?'} avatarSlug={readerAvatarSlug} size="xl" />
                <div className="flex-1">
                  <FormField
                    label="Reader's first name"
                    required
                    value={readerName}
                    onChange={(e) => setReaderName(e.target.value)}
                    autoFocus
                    maxLength={120}
                    placeholder="e.g. Ollie"
                  />
                </div>
              </div>
              <ReaderAvatarPicker
                value={readerAvatarSlug}
                onChange={setReaderAvatarSlug}
                name={readerName}
                disabled={creatingReader}
              />
              <div className="mt-2 flex items-center justify-between">
                <button
                  type="button"
                  className="text-[13px] font-semibold text-or-slate-500 hover:text-or-orange-700"
                  onClick={() => setStep(1)}
                  disabled={creatingReader}
                >
                  ← Back
                </button>
                <Button type="submit" size="lg" disabled={creatingReader}>
                  {creatingReader ? 'Creating…' : 'Continue'}
                </Button>
              </div>
            </form>
          ) : null}

          {step === 3 ? (
            <div className="flex flex-col gap-6">
              <Header
                eyebrow="Step 3 of 3"
                title={`What's ${createdReader?.name ?? 'your reader'}'s reading level?`}
                subtitle="Pick any that fit — readers often span a range. We'll use this to tailor their shelf."
              />

              {!labelsLoaded ? (
                <LabelSkeleton />
              ) : (
                <ReadingLevelPicker
                  levels={readingLevels}
                  selectedIds={selectedReadingLevelIds}
                  onToggle={toggleReadingLevel}
                />
              )}

              <div className="mt-2 flex items-center justify-between">
                <button
                  type="button"
                  className="text-[13px] font-semibold text-or-slate-500 hover:text-or-orange-700"
                  onClick={() => setStep(2)}
                  disabled={savingLabels}
                >
                  ← Back
                </button>
                <Button type="button" size="lg" onClick={finish} disabled={savingLabels}>
                  {savingLabels ? 'Saving…' : 'Finish'}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Header({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div>
      <div className="or-kicker">{eyebrow}</div>
      <h1 className="mt-1 font-display text-[26px] font-bold text-or-slate-900">{title}</h1>
      <p className="mt-1 text-[14px] text-or-slate-600">{subtitle}</p>
    </div>
  );
}

function StepIndicator({ step }: { step: 1 | 2 | 3 }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Onboarding progress">
      {[1, 2, 3].map((n) => (
        <li key={n} className="flex items-center gap-2">
          <span
            aria-current={step === n ? 'step' : undefined}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border text-[12px] font-bold transition-colors',
              step === n
                ? 'border-transparent bg-or-orange-500 text-white'
                : step > n
                  ? 'border-or-orange-200 bg-or-orange-100 text-or-orange-700'
                  : 'border-or-slate-200 bg-white text-or-slate-500',
            )}
          >
            {n}
          </span>
          {n < 3 ? <span className="h-[2px] w-8 rounded-full bg-or-slate-200" aria-hidden /> : null}
        </li>
      ))}
    </ol>
  );
}

function ReadingLevelPicker({
  levels,
  selectedIds,
  onToggle,
}: {
  levels: LabelView[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <section>
      <div className="mb-2">
        <div className="font-display text-[15px] font-bold text-or-slate-900">Reading level</div>
        <div className="text-[12px] text-or-slate-500">
          Pick any that fit — readers often span a range.
        </div>
      </div>
      <LabelChipGrid
        labels={levels}
        selectedIds={selectedIds}
        onToggle={onToggle}
        emptyLabel="No reading levels configured yet."
      />
    </section>
  );
}

function LabelSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <span
          key={i}
          className="h-8 w-20 animate-pulse rounded-full bg-or-slate-100"
          aria-hidden
        />
      ))}
    </div>
  );
}
